/**
 * Tenant Management Types
 * TypeScript interfaces for O365 tenant registration and management.
 * Matches backend Pydantic models in app/models/tenant.py
 */

/**
 * Tenant management status enumeration
 * Represents the management status of tenants within the application,
 * not the operational status in Azure AD.
 *
 * The HOME status identifies the MSP/partner's own tenant where staff members
 * belong. This tenant is foundational to the app and cannot be paused,
 * archived, or deleted. Superadmin actions require membership in the home tenant.
 */
export enum TenantStatus {
  HOME = 'home',          // The MSP/partner home tenant (cannot be paused/archived/deleted)
  MANAGED = 'managed',    // Actively managing this customer tenant
  PAUSED = 'paused',      // Temporarily suspended management
  ARCHIVED = 'archived',  // No longer managing, keeping record
}

/**
 * Request payload for registering a new tenant.
 * tenant_id is optional - if not provided, it will be auto-discovered from the domain.
 */
export interface TenantRegistrationRequest {
  tenant_id?: string;
  tenant_name: string;
  domain: string;
  notes?: string;
  organization_id?: number;
}

/**
 * Complete tenant details with full metadata
 */
export interface TenantDetails {
  id: number;
  tenant_id: string;
  tenant_name: string;
  domain: string;
  status: TenantStatus;
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: number;
  created_by_email: string | null;
  last_admin_consent: string | null;
}

/**
 * Simplified tenant info for list views
 */
export interface TenantListItem {
  id: number;
  tenant_id: string;
  tenant_name: string;
  domain: string;
  status: TenantStatus;
  created_at: string;
  has_access: boolean | null;
  notes: string | null;
}

/**
 * Tenant pending B2B invitation redemption.
 * Federated organization users must redeem B2B invitation before accessing tenant.
 */
export interface PendingTenant {
  tenant_id: string;
  tenant_name: string;
  invite_redeem_url: string | null;
  granted_at: string | null;
}

/**
 * Paginated tenant list response
 */
export interface PaginatedTenants {
  items: TenantListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  pending_tenants: PendingTenant[];
}

/**
 * Request payload for updating tenant metadata
 */
export interface TenantUpdateRequest {
  tenant_name?: string;
  notes?: string;
  status?: TenantStatus;
}

/**
 * App requiring admin consent
 */
export interface MissingApp {
  client_id: string;
  name: string;
}

/**
 * Result of tenant access verification via Graph API
 */
export interface TenantAccessVerification {
  tenant_id: string;
  has_access: boolean;
  organization_name: string | null;
  verified_at: string;
  error_message: string | null;
  missing_apps: MissingApp[] | null;
}

/**
 * Request payload for granting tenant access to a user
 */
export interface GrantTenantAccessRequest {
  matter_role?: string;
}

/**
 * Response from granting tenant access to a user
 */
export interface TenantAccessResponse {
  user_id: number;
  tenant_id: string;
  granted_at: string;
  granted_by: number;
  guest_object_id: string | null;
  guest_provisioned_at: string | null;
  matter_role: string | null;
}

/**
 * User with tenant access details for list display
 */
export interface TenantUserWithAccess {
  user_id: number;
  user_name: string;
  user_email: string;
  organization_id: number;
  organization_name: string;
  accessible_tenant_ids: string[];
  granted_at: string;
  granted_by_name: string;
  matter_role: string | null;
  guest_object_id: string | null;
  guest_provisioned_at: string | null;
}

/**
 * Status codes for UPN analysis response
 */
export type AnalyzeUpnStatus =
  | 'can_grant'
  | 'create_in_org'
  | 'can_grant_cross_org'
  | 'other_org_no_user'
  | 'create_tenant_member';

/**
 * License usage information for an organization
 */
export interface LicenseInfo {
  org_used: number;
  org_cap: number | null;
}

/**
 * User info returned from UPN analysis
 */
export interface AnalyzeUpnUserInfo {
  id: number;
  name: string;
  email: string;
  organization_name: string;
}

/**
 * Response from analyzing a UPN for tenant access grant
 */
export interface AnalyzeUpnResponse {
  status: AnalyzeUpnStatus;
  user: AnalyzeUpnUserInfo | null;
  other_org_name: string | null;
  license_info: LicenseInfo;
}

/**
 * Response from revoking tenant access from a user
 */
export interface RevokeAccessResponse {
  success: boolean;
  user_id: number;
  tenant_id: string;
  message: string | null;
}

/**
 * Response from completing B2B invitation redemption.
 * The endpoint verifies redemption via Graph API and returns
 * the verification result.
 */
export interface RedemptionCompletionResponse {
  /** Status of redemption: "completed" or "pending" */
  status: 'completed' | 'pending';
  tenant_id: string;
  /** Identity type after redemption (e.g., "ExternalAzureAD") */
  identity_type?: string;
  /** Message explaining the status or next steps */
  message?: string;
}
